import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/cloudflareService';
import * as storageService from '../../services/storageService';
import { HospitalGroup, ScreenSession } from '../../types';
import {
    Building2, Users, LayoutGrid, LogOut, Activity, ShieldCheck, Plus, Monitor,
    Sunrise, Star, ChevronLeft, ChevronRight, Sun, Moon, TrendingUp,
    AlertTriangle, CheckCircle2, Heart, Wifi, WifiOff, ExternalLink,
    BarChart2, Clock, Stethoscope, RefreshCw, ScrollText, DollarSign, Megaphone,
    CalendarDays, MessageSquare, Zap, ChevronDown, History, Settings,
    Tv, Tablet, Smartphone, Laptop, Power, X, Edit, Trash2, Eye, EyeOff
} from 'lucide-react';
import { AdminNavbarUser } from '../../components/AdminNavbarUser';
import { motion, AnimatePresence } from 'framer-motion';
import ClientBillingView from '../../components/ClientBillingView';
import ExecutiveMarketing from '../../components/ExecutiveMarketing';
import StaffSessionsPanel from '../../components/StaffSessionsPanel';
import AnimatedNumber from '../../components/shared/AnimatedNumber';
import GroupAnalytics from './GroupAnalytics';
import GroupSettings from './GroupSettings';
import Sparkline from '../../components/shared/Sparkline';
import BugReportWidget from '../../components/BugReportWidget';
import { t, cardClass as sharedCardClass, premiumCardClass } from '../../hooks/useAdminTheme';

interface HospitalMetrics {
    patients: number;
    rooms: number;
    screensOnline: number;
    screensOffline: number;
    avgRating: number | null;
    totalFeedback: number;
    todaySurgeries: number;
}

interface EnrichedHospital {
    id: string;
    name: string;
    handle: string;
    logoUrl?: string;
    status: 'active' | 'inactive';
    metrics: HospitalMetrics;
    preferences?: any;
    features?: string[];
}

interface AggregateMetrics {
    totalPatients: number;
    totalRooms: number;
    totalScreensOnline: number;
    totalScreensOffline: number;
    avgRating: number | null;
    totalFeedback: number;
    todaySurgeries: number;
}

interface FeedbackItem {
    id: string;
    rating: number;
    message: string | null;
    createdAt: string;
    hospitalName?: string;
}

const GroupAdminDashboard: React.FC = () => {
    const { handle } = useParams<{ handle: string }>();
    const navigate = useNavigate();

    const [group, setGroup] = useState<HospitalGroup | null>(null);
    const [hospitals, setHospitals] = useState<EnrichedHospital[]>([]);
    const [metrics, setMetrics] = useState<AggregateMetrics>({
        totalPatients: 0, totalRooms: 0, totalScreensOnline: 0,
        totalScreensOffline: 0, avgRating: null, totalFeedback: 0, todaySurgeries: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [recentFeedback, setRecentFeedback] = useState<FeedbackItem[]>([]);
    
    const auth = storageService.getAuthData();

    const [activeTab, setActiveTabRaw] = useState<'brief' | 'hospitals' | 'screens' | 'staff_sessions' | 'billing' | 'marketing' | 'history' | 'analytics' | 'settings'>(() => {
        const saved = localStorage.getItem('hs_groupadmin_tab') as any;
        if (saved === 'ads') return 'marketing';
        return saved || 'brief';
    });
    const setActiveTab = (t: any) => {
        localStorage.setItem('hs_groupadmin_tab', t);
        setActiveTabRaw(t);
    };

    const [historyData, setHistoryData] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyFilterHospital, setHistoryFilterHospital] = useState<string | null>(null);
    const [historyDateRange, setHistoryDateRange] = useState({ start: '', end: '' });
    const [historyExpandedPatientId, setHistoryExpandedPatientId] = useState<string | null>(null);

    const [screens, setScreens] = useState<ScreenSession[]>([]);
    const [screenFilter, setScreenFilter] = useState<'all' | 'online' | 'offline'>('all');
    const [screenHospitalFilter, setScreenHospitalFilter] = useState<string | null>(null);
    const [editingScreen, setEditingScreen] = useState<ScreenSession | null>(null);
    const [editScreenName, setEditScreenName] = useState('');
    const [editScreenGroup, setEditScreenGroup] = useState('');
    const [editForcedTheme, setEditForcedTheme] = useState<string | null>(null);
    const [editScreenMode, setEditScreenMode] = useState<string>('BOARD');
    const [editMediaUrl, setEditMediaUrl] = useState('');
    const [editBoardLayout, setEditBoardLayout] = useState<string>('LIST');
    const [previewScreen, setPreviewScreen] = useState<ScreenSession | null>(null);

    // Yin-Yang: Fixed theme — sidebar always dark navy, content always light
    const theme = 'light' as const;

    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        return localStorage.getItem('hs_group_sidebar') !== 'closed';
    });

    useEffect(() => {
        localStorage.setItem('hs_group_sidebar', isSidebarOpen ? 'open' : 'closed');
    }, [isSidebarOpen]);

    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

    // Design tokens imported from centralized useAdminTheme ("t" object)
    // cardClass for content cards with padding
    const cardClass = `${sharedCardClass} p-6 relative overflow-hidden`;

    const fetchGroupData = async () => {
        try {
            if (!handle) return;
            setIsLoading(true);
            const [res, screensData] = await Promise.all([
                apiFetch(`/api/groups/${handle}`, { credentials: 'include' }),
                storageService.getScreenSessions()
            ]);

            if (res.status === 401 || res.status === 403) {
                window.dispatchEvent(new Event('auth:expired'));
                return;
            }

            if (!res.ok) throw new Error('No se pudo cargar la información del corporativo.');

            const data = await res.json();
            if (data.success) {
                setGroup({ ...data.groupProfile, createdAt: new Date().toISOString() });
                setHospitals(data.hospitals || []);
                setMetrics(data.aggregateMetrics || {
                    totalPatients: 0, totalRooms: 0, totalScreensOnline: 0,
                    totalScreensOffline: 0, avgRating: null, totalFeedback: 0, todaySurgeries: 0,
                });
                setScreens(screensData as ScreenSession[]);
                setLastRefresh(new Date());
            } else {
                setError('Corporate Group not found');
            }
        } catch (err: any) {
            console.error("Group fetch error:", err);
            setError('Error conectando al Búnker Edge');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchGroupData(); }, [handle]);

    // Auto-refresh every 60s
    useEffect(() => {
        const interval = setInterval(() => { fetchGroupData(); }, 60000);
        return () => clearInterval(interval);
    }, [handle]);

    // Fetch recent feedback from all hospitals
    useEffect(() => {
        if (hospitals.length === 0) return;
        const fetchFeedback = async () => {
            const allFb: FeedbackItem[] = [];
            for (const h of hospitals.slice(0, 5)) {
                try {
                    const res = await apiFetch(`/api/feedback?hospitalId=${h.id}`, { credentials: 'include' });
                    if (res.ok) {
                        const items = await res.json();
                        if (Array.isArray(items)) {
                            allFb.push(...items.slice(0, 5).map((i: any) => ({ ...i, hospitalName: h.name })));
                        }
                    }
                } catch { }
            }
            setRecentFeedback(allFb.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        };
        fetchFeedback();
    }, [hospitals]);

    useEffect(() => {
        if (activeTab === 'history' && hospitals.length > 0) {
            const loadHistory = async () => {
                setHistoryLoading(true);
                try {
                    const results = await Promise.all(
                        hospitals.map(h => apiFetch(`/api/patients/hospital-history?hospitalId=${h.id}`, { credentials: 'include' }).then(r => r.ok ? r.json() : []))
                    );
                    const allPatients = results.flat();
                    setHistoryData(allPatients);
                } catch (e) {
                    console.error("Error fetching history", e);
                }
                setHistoryLoading(false);
            };
            loadHistory();
        }
    }, [activeTab, hospitals]);

    const filteredHistory = useMemo(() => {
        return historyData.filter(p => {
            if (historyFilterHospital && p.hospitalId !== historyFilterHospital) return false;
            if (historyDateRange.start) {
                const start = new Date(historyDateRange.start);
                start.setHours(0, 0, 0, 0);
                if (new Date(p.admissionTime) < start) return false;
            }
            if (historyDateRange.end) {
                const end = new Date(historyDateRange.end);
                end.setHours(23, 59, 59, 999);
                if (new Date(p.admissionTime) > end) return false;
            }
            return true;
        }).sort((a, b) => new Date(b.admissionTime).getTime() - new Date(a.admissionTime).getTime());
    }, [historyData, historyFilterHospital, historyDateRange]);

    const handleLogout = () => navigate('/');

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Buenos días';
        if (hour < 19) return 'Buenas tardes';
        return 'Buenas noches';
    };

    const formattedDate = new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    // Weekly trend from todaySurgeries (mock: spread equally for sparkline — real data comes in Phase 2)
    const weeklyTrend = useMemo(() => {
        // Generate a reasonable sparkline from aggregate metrics
        const base = metrics.todaySurgeries;
        return [
            Math.max(0, base - 2), Math.max(0, base + 1), Math.max(0, base - 1),
            Math.max(0, base + 3), Math.max(0, base), Math.max(0, base + 2), base
        ];
    }, [metrics.todaySurgeries]);

    // Top hospital today
    const topHospitalToday = useMemo(() => {
        if (hospitals.length === 0) return null;
        const sorted = [...hospitals].sort((a, b) => b.metrics.todaySurgeries - a.metrics.todaySurgeries);
        if (sorted[0]?.metrics.todaySurgeries === 0) return null;
        return sorted[0];
    }, [hospitals]);

    // Alerts
    const offlineScreens = hospitals.filter(h => h.metrics.screensOffline > 0);
    const alerts = useMemo(() => {
        const list: { type: 'warning' | 'danger' | 'info'; message: string; action?: string }[] = [];
        if (metrics.totalScreensOffline > 0) {
            list.push({ type: 'danger', message: `${metrics.totalScreensOffline} pantalla${metrics.totalScreensOffline > 1 ? 's' : ''} sin señal`, action: 'screens' });
        }
        if (metrics.totalPatients > 0) {
            list.push({ type: 'info', message: `${metrics.totalPatients} paciente${metrics.totalPatients > 1 ? 's' : ''} activos ahora mismo` });
        }
        return list;
    }, [metrics]);

    if (isLoading) {
        return (
            <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full" />
                    <p className={`text-sm font-bold ${t.textMuted} animate-pulse`}>Cargando Command Center...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`min-h-screen ${t.bg} flex flex-col items-center justify-center ${t.textWhite}`}>
                <ShieldCheck className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-xl font-bold">Acceso Restringido</h2>
                <p className={`${t.textMuted} mt-2`}>{error}</p>
                <button onClick={() => navigate('/hq')} className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold">Volver al HQ</button>
            </div>
        );
    }

    if (!group) return null;

    // Modules Entitlements
    const isSuperAdmin = auth?.role === 'SUPER_ADMIN';
    // Group has access if ANY of its hospitals have the module (or legacy fallback)
    const hasAdsModule = hospitals.some(h => !h.features || h.features.includes('ADS'));
    const canSeeAdsModule = isSuperAdmin || hasAdsModule;

    // Sidebar navigation items
    const sidebarSections = [
        {
            label: 'General',
            items: [
                { id: 'brief', label: 'Resumen', icon: Sunrise },
            ]
        },
        {
            label: 'Operaciones',
            items: [
                { id: 'hospitals', label: 'Sucursales', icon: Building2, badge: hospitals.length },
                { id: 'screens', label: 'Pantallas', icon: Monitor, badge: metrics.totalScreensOffline > 0 ? metrics.totalScreensOffline : null, badgeColor: 'red' },
                { id: 'staff_sessions', label: 'Staff', icon: Users },
                { id: 'settings', label: 'Configuración', icon: Settings },
                { id: 'history', label: 'Historial', icon: History },
            ]
        },
        {
            label: 'Inteligencia',
            items: [
                { id: 'analytics', label: 'Analytics', icon: BarChart2 },
            ]
        },
        {
            label: 'Negocio',
            items: [
                { id: 'marketing', label: 'Marketing', icon: Megaphone, locked: !hasAdsModule },
                { id: 'billing', label: 'Finanzas', icon: DollarSign },
            ]
        },
    ];

    return (
        <div className={`min-h-screen ${t.bg} font-sans relative overflow-hidden flex flex-col xl:flex-row ${t.textMain}`}>
            {/* Ambient Background Orbs */}
            <div className="fixed top-0 right-0 w-1/2 h-96 bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none z-0"></div>
            <div className="fixed bottom-0 left-0 w-1/2 h-96 bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none z-0"></div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* Desktop Left Sidebar (Yin-Yang: always dark navy)         */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <aside className={`hidden xl:flex flex-col ${isSidebarOpen ? 'w-[260px]' : 'w-[72px]'} fixed inset-y-0 left-0 z-40 ${t.sidebarBg} border-r ${t.sidebarBorder} transition-all duration-300 ease-in-out`}>
                {/* Logo / Group Name */}
                <div className={`px-4 pt-6 pb-5 shrink-0 flex items-center ${isSidebarOpen ? 'gap-3' : 'justify-center'} transition-all border-b ${t.sidebarDivider}`}>
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-2.5 rounded-xl shadow-[0_0_20px_rgba(99,102,241,0.3)] border border-indigo-400/20 flex-shrink-0">
                        <Building2 className="w-5 h-5 text-white" />
                    </div>
                    {isSidebarOpen && (
                        <div className="min-w-0">
                            <h1 className="text-[15px] font-black text-white tracking-tight truncate">{group.name}</h1>
                            <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">Command Center</p>
                        </div>
                    )}
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto px-3 pt-4 space-y-0.5 scrollbar-hide">
                    {sidebarSections.map((section, si) => (
                        <React.Fragment key={section.label}>
                            {/* Section separator */}
                            {si > 0 && (
                                <>
                                    {isSidebarOpen && <div className="pt-4 pb-1.5 px-3"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">{section.label}</p></div>}
                                    {!isSidebarOpen && <div className="w-6 h-px bg-white/[0.06] mx-auto my-3" />}
                                </>
                            )}
                            {si === 0 && isSidebarOpen && <div className="pb-1.5 px-3"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">{section.label}</p></div>}

                            {section.items.map(item => {
                                const isActive = activeTab === item.id;
                                const isComingSoon = (item as any).comingSoon;
                                const isLocked = (item as any).locked && !isSuperAdmin;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => !isComingSoon && !isLocked && setActiveTab(item.id as any)}
                                        title={!isSidebarOpen ? item.label : undefined}
                                        className={`w-full relative ${isSidebarOpen ? 'px-3 py-2.5 text-left gap-3' : 'py-2.5 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all duration-200 flex items-center ${
                                            isActive
                                                ? t.sidebarActive
                                                : isComingSoon || isLocked
                                                    ? 'text-slate-600 border-l-[3px] border-transparent cursor-not-allowed opacity-50'
                                                    : `${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`
                                        }`}
                                    >
                                        <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                                        {isSidebarOpen && <span className="whitespace-nowrap flex-1">{item.label}</span>}
                                        {isSidebarOpen && isLocked && (
                                            <div className="bg-amber-500/10 border border-amber-500/20 text-amber-500 rounded px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest flex-shrink-0" title="Módulo restringido (Visible solo para Super Admin)">LOCKED</div>
                                        )}
                                        {isSidebarOpen && (item as any).badge && !isLocked && (
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                                                (item as any).badgeColor === 'red'
                                                    ? (isActive ? 'bg-red-500/20 text-red-300' : 'bg-red-500/10 text-red-400')
                                                    : (isActive ? 'bg-white/15 text-white' : 'bg-white/[0.08] text-slate-400')
                                            }`}>{(item as any).badge}</span>
                                        )}
                                        {isSidebarOpen && isComingSoon && (
                                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">PRONTO</span>
                                        )}
                                    </button>
                                );
                            })}
                        </React.Fragment>
                    ))}

                    {/* Resources */}
                    {isSidebarOpen && <div className="pt-5 pb-1.5 px-3"><p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">Recursos</p></div>}
                    {!isSidebarOpen && <div className="w-6 h-px bg-white/[0.06] mx-auto my-3" />}
                    <a
                        href="/docs/admin_preview" target="_blank" rel="noopener noreferrer"
                        title={!isSidebarOpen ? 'API Docs' : undefined}
                        className={`w-full ${isSidebarOpen ? 'px-3 py-2.5 text-left gap-3' : 'py-2.5 justify-center text-center'} rounded-lg text-[13px] font-semibold transition-all flex items-center ${t.sidebarText} ${t.sidebarHover} border-l-[3px] border-transparent`}
                    >
                        <ScrollText className="w-[18px] h-[18px] flex-shrink-0" />
                        {isSidebarOpen && <span className="whitespace-nowrap">API Docs</span>}
                    </a>
                </div>

                {/* User Widget + Footer */}
                <div className={`shrink-0 border-t ${t.sidebarDivider}`}>
                    <div className="px-3 pt-3 pb-2">
                        <AdminNavbarUser theme="dark" variant="sidebar" collapsed={!isSidebarOpen} />
                    </div>
                    <div className={`px-3 pb-3 flex ${isSidebarOpen ? 'items-center justify-between' : 'flex-col gap-2 items-center'}`}>
                        <button 
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
                            title={isSidebarOpen ? 'Colapsar menú' : 'Expandir menú'}
                            className="p-2 rounded-lg transition-all text-slate-500 hover:text-slate-300 hover:bg-white/[0.05]"
                        >
                            {isSidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                        <button 
                            onClick={handleLogout} 
                            title="Cerrar sesión"
                            className="p-2 rounded-lg transition-all text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* Mobile Top Navbar (Visible only < xl)                      */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <nav className={`xl:hidden w-full bg-white/90 backdrop-blur-2xl ${t.borderBottom} sticky top-0 z-40 flex flex-col`}>
                <div className="w-full px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-2 rounded-xl shadow-[0_0_15px_rgba(99,102,241,0.3)] border border-indigo-400/20">
                            <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className={`text-lg font-black ${t.textWhite} tracking-tight`}>{group.name}</h1>
                            <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">Command Center</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => { fetchGroupData(); }} className={`p-2 rounded-xl ${t.textMuted} hover:text-indigo-500 transition-all`}>
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={handleLogout} className={`p-2 rounded-xl ${t.textMuted} hover:text-red-400 hover:bg-red-400/10 transition-all`}>
                            <LogOut className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Mobile / Tablet Tabs */}
                <div className="w-full px-4 pb-3 overflow-x-auto scrollbar-hide flex gap-2">
                    {[
                        { id: 'brief', label: 'Resumen', icon: Sunrise },
                        { id: 'hospitals', label: 'Sucursales', icon: Building2 },
                        { id: 'screens', label: 'Pantallas', icon: Monitor },
                        { id: 'staff_sessions', label: 'Staff', icon: Users },
                        { id: 'marketing', label: 'Marketing', icon: Megaphone },
                        { id: 'billing', label: 'Finanzas', icon: DollarSign },
                    ].map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id as any)}
                            className={`px-4 py-2 whitespace-nowrap flex-shrink-0 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === item.id ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-500 hover:text-slate-900 shadow-sm border border-slate-200'}`}
                        >
                            <item.icon className="w-4 h-4" /> {item.label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* Main Content Area                                          */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <main className={`flex-1 ${isSidebarOpen ? 'xl:ml-[260px] xl:max-w-[calc(100vw-260px)]' : 'xl:ml-[72px] xl:max-w-[calc(100vw-72px)]'} w-full max-w-[100vw] min-h-screen relative z-10 transition-all duration-300 ease-in-out`}>
                <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-8 lg:py-12">

                    {/* ══════════════════════════════════════════ */}
                    {/* BRIEFING TAB (Premium)                     */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'brief' && (
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
                            className="space-y-8"
                        >
                            {/* Header */}
                            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-100 shadow-sm">
                                            <Sunrise className="w-6 h-6 text-amber-500" />
                                        </div>
                                        <div>
                                            <h2 className={`text-2xl font-black ${t.textWhite} leading-tight tracking-tight`}>{getGreeting()}</h2>
                                            <p className={`text-sm ${t.textMuted} font-medium capitalize`}>{formattedDate}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`text-[10px] font-mono ${t.textMuted}`}>
                                        Actualizado: {lastRefresh.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <button
                                        onClick={() => fetchGroupData()}
                                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm text-slate-500 hover:text-slate-800 active:scale-95"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Actualizar
                                    </button>
                                </div>
                            </motion.div>

                            {/* Quick Stats Row */}
                            <motion.div variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }} className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                {/* Surgeries Today */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 cursor-pointer group flex flex-col justify-between`}
                                >
                                    <div>
                                        <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 w-fit mb-4 group-hover:-translate-y-0.5 group-hover:shadow-sm transition-all duration-300">
                                            <CalendarDays className="w-5 h-5" />
                                        </div>
                                        <p className={`text-4xl font-black ${t.textWhite} tracking-tighter mb-0.5`}>
                                            <AnimatedNumber value={metrics.todaySurgeries} />
                                        </p>
                                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Cirugías Hoy</p>
                                    </div>
                                    <div className="mt-4 opacity-40 group-hover:opacity-80 transition-opacity duration-300">
                                        <Sparkline data={weeklyTrend} color="#10b981" height={28} />
                                    </div>
                                </motion.div>

                                {/* Active Patients */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 cursor-pointer group`}
                                >
                                    <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 w-fit mb-4 group-hover:-translate-y-0.5 group-hover:shadow-sm transition-all duration-300">
                                        <Activity className="w-5 h-5" />
                                    </div>
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}>
                                            <AnimatedNumber value={metrics.totalPatients} />
                                        </p>
                                        {metrics.totalPatients > 0 && (
                                            <span className="relative flex h-2.5 w-2.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                            </span>
                                        )}
                                    </div>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Pacientes Activos</p>
                                </motion.div>

                                {/* Satisfaction */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 cursor-pointer group`}
                                    onClick={() => setActiveTab('marketing')}
                                >
                                    <div className="p-2 rounded-xl bg-amber-50 text-amber-600 border border-amber-100 w-fit mb-4 group-hover:-translate-y-0.5 group-hover:shadow-sm transition-all duration-300">
                                        <Star className="w-5 h-5" />
                                    </div>
                                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter mb-0.5`}>
                                        {metrics.avgRating ? `${metrics.avgRating}` : '—'}
                                    </p>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Satisfacción</p>
                                    {metrics.avgRating && (
                                        <div className="flex items-center gap-0.5 mt-3">
                                            {[1, 2, 3, 4, 5].map(s => (
                                                <Star
                                                    key={s}
                                                    className={`w-3.5 h-3.5 ${s <= Math.round(metrics.avgRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                                                />
                                            ))}
                                            {metrics.totalFeedback > 0 && (
                                                <span className={`text-[10px] ${t.textMuted} font-bold ml-1.5`}>({metrics.totalFeedback})</span>
                                            )}
                                        </div>
                                    )}
                                </motion.div>

                                {/* Screens */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 cursor-pointer group`}
                                    onClick={() => setActiveTab('screens')}
                                >
                                    <div className="p-2 rounded-xl bg-cyan-50 text-cyan-600 border border-cyan-100 w-fit mb-4 group-hover:-translate-y-0.5 group-hover:shadow-sm transition-all duration-300">
                                        <Monitor className="w-5 h-5" />
                                    </div>
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}>
                                            <AnimatedNumber value={metrics.totalScreensOnline} />
                                        </p>
                                        {metrics.totalScreensOffline > 0 && (
                                            <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">{metrics.totalScreensOffline} off</span>
                                        )}
                                    </div>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Pantallas Online</p>
                                </motion.div>

                                {/* Hospitals */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 cursor-pointer group`}
                                    onClick={() => setActiveTab('hospitals')}
                                >
                                    <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 w-fit mb-4 group-hover:-translate-y-0.5 group-hover:shadow-sm transition-all duration-300">
                                        <Building2 className="w-5 h-5" />
                                    </div>
                                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter mb-0.5`}>
                                        <AnimatedNumber value={hospitals.length} />
                                    </p>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Sucursales</p>
                                </motion.div>
                            </motion.div>

                            {/* Bottom Row: Alerts + Recent Feedback + Top Hospital */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Alerts Panel */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${cardClass} lg:col-span-1 flex flex-col`}
                                >
                                    <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-5 flex items-center gap-2`}>
                                        <AlertTriangle className={`w-3.5 h-3.5 ${alerts.some(a => a.type === 'danger') ? 'text-red-500' : 'text-amber-500'}`} />
                                        Estado de la Red
                                        {alerts.length > 0 && (
                                            <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${alerts.some(a => a.type === 'danger') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                                                {alerts.length}
                                            </span>
                                        )}
                                    </h3>
                                    {alerts.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-6 text-center">
                                            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                                                <Heart className="w-6 h-6 text-emerald-500" />
                                            </div>
                                            <p className={`text-sm font-bold ${t.textWhite}`}>Toda la red operando</p>
                                            <p className={`text-xs ${t.textMuted} mt-1`}>
                                                {metrics.totalScreensOnline} pantalla{metrics.totalScreensOnline !== 1 ? 's' : ''} activa{metrics.totalScreensOnline !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {alerts.map((alert, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => alert.action && setActiveTab(alert.action as any)}
                                                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all ${
                                                        alert.type === 'danger'
                                                            ? 'bg-red-50 border border-red-200 hover:bg-red-100'
                                                            : alert.type === 'warning'
                                                            ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100'
                                                            : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                                    }`}
                                                >
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                                        alert.type === 'danger' ? 'bg-red-400 animate-pulse' :
                                                        alert.type === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
                                                    }`} />
                                                    <span className={`text-sm font-medium ${t.textWhite} flex-1`}>{alert.message}</span>
                                                    {alert.action && <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>

                                {/* Recent Feedback */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${premiumCardClass} p-6 lg:col-span-1 cursor-pointer group flex flex-col`}
                                    onClick={() => setActiveTab('marketing')}
                                >
                                    <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-5 flex items-center gap-2`}>
                                        <MessageSquare className="w-3.5 h-3.5 text-indigo-500" />
                                        Últimas Opiniones
                                        <ChevronRight className={`w-3.5 h-3.5 text-indigo-400 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300`} />
                                    </h3>
                                    {recentFeedback.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-6 text-center">
                                            <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                                                <MessageSquare className="w-6 h-6 text-indigo-400" />
                                            </div>
                                            <p className={`text-sm font-bold ${t.textWhite}`}>Sin opiniones aún</p>
                                            <p className={`text-xs ${t.textMuted} mt-1`}>Las calificaciones aparecerán aquí</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {recentFeedback.slice(0, 4).map((fb, i) => (
                                                <div key={fb.id || i} className="flex items-start gap-3 p-3 rounded-xl transition-colors bg-slate-50 border border-slate-100 hover:bg-slate-100">
                                                    <div className="flex gap-0.5 flex-shrink-0 mt-0.5">
                                                        {[1, 2, 3, 4, 5].map(s => (
                                                            <Star key={s} className={`w-3 h-3 ${s <= fb.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                                                        ))}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        {fb.message && <p className={`text-xs ${t.textMain} line-clamp-2`}>{fb.message}</p>}
                                                        <p className={`text-[10px] ${t.textMuted} mt-1`}>
                                                            {fb.hospitalName} • {new Date(fb.createdAt).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {recentFeedback.length > 0 && (
                                        <div className="mt-4 w-full text-center py-2 text-xs font-bold uppercase tracking-widest rounded-xl text-indigo-600">
                                            Ver todas →
                                        </div>
                                    )}
                                </motion.div>

                                {/* Top Hospital + Screen Health */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className={`${cardClass} lg:col-span-1 flex flex-col justify-between`}
                                >
                                    {/* Top Sucursal */}
                                    <div className="mb-8 cursor-pointer group/top" onClick={() => setActiveTab('hospitals')}>
                                        <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-4 flex items-center gap-2`}>
                                            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                                            Sucursal Más Activa Hoy
                                            <ChevronRight className={`w-3.5 h-3.5 text-emerald-400 ml-auto opacity-0 group-hover/top:opacity-100 group-hover/top:translate-x-0.5 transition-all duration-300`} />
                                        </h3>
                                        {topHospitalToday ? (
                                            <div className="flex items-center gap-4 p-4 rounded-xl transition-colors bg-emerald-50 border border-emerald-200 hover:bg-emerald-100">
                                                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center p-2 shadow-sm">
                                                    {topHospitalToday.logoUrl ? (
                                                        <img src={topHospitalToday.logoUrl} className="w-full h-full object-contain" alt="" />
                                                    ) : (
                                                        <Building2 className="w-6 h-6 text-emerald-500" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-bold ${t.textWhite} truncate`}>{topHospitalToday.name}</p>
                                                    <p className="text-xs text-emerald-600 font-bold">{topHospitalToday.metrics.todaySurgeries} cirugía{topHospitalToday.metrics.todaySurgeries > 1 ? 's' : ''} hoy</p>
                                                </div>
                                                <Zap className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors">
                                                <Stethoscope className={`w-5 h-5 ${t.textMuted}`} />
                                                <p className={`text-sm ${t.textMuted} font-medium`}>Sin actividad hoy todavía</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Actividad por Sucursal */}
                                    <div>
                                        <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-3 flex items-center gap-2`}>
                                            <BarChart2 className="w-3.5 h-3.5 text-indigo-500" />
                                            Actividad por Sucursal
                                        </h3>
                                        <div className="space-y-2">
                                            {[...hospitals].sort((a, b) => b.metrics.patients - a.metrics.patients).slice(0, 4).map(h => (
                                                <div key={h.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600">
                                                        <Building2 className="w-4 h-4" />
                                                    </div>
                                                    <p className={`text-sm font-bold ${t.textWhite} truncate flex-1`}>{h.name}</p>
                                                    <div className="flex items-center gap-3 flex-shrink-0">
                                                        <span className={`text-xs font-bold ${t.textMuted}`}>{h.metrics.patients} pac.</span>
                                                        {h.metrics.avgRating && (
                                                            <span className="text-xs font-bold text-amber-500 flex items-center gap-0.5">
                                                                <Star className="w-3 h-3 fill-current" />{h.metrics.avgRating}
                                                            </span>
                                                        )}
                                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.metrics.screensOffline > 0 ? 'bg-red-400' : 'bg-emerald-400'}`} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'settings' && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={premiumCardClass + " p-6"}>
                            <GroupSettings />
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* HOSPITALS TAB                              */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'hospitals' && (
                        <motion.div
                            initial="hidden"
                            animate="show"
                            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } }}
                            className="space-y-6"
                        >
                            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className="flex items-center justify-between">
                                <div>
                                    <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-1`}>Red Hospitalaria</p>
                                    <h2 className={`text-xl font-black ${t.textWhite} tracking-tight`}>Sucursales de la Red</h2>
                                    <p className={`text-xs ${t.textMuted} font-medium mt-1`}>{hospitals.length} hospital{hospitals.length !== 1 ? 'es' : ''} conectado{hospitals.length !== 1 ? 's' : ''}</p>
                                </div>
                            </motion.div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {hospitals.map((hospital, idx) => (
                                    <motion.div
                                        key={hospital.id}
                                        variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                        className={`bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-[2px] transition-all duration-300 group overflow-hidden`}
                                    >
                                        {/* Top accent bar */}
                                        <div className={`h-0.5 w-full ${hospital.metrics.screensOffline > 0 ? 'bg-gradient-to-r from-amber-400 to-red-400' : 'bg-gradient-to-r from-indigo-400 to-emerald-400'}`} />

                                        <div className="p-6">
                                            {/* Header */}
                                            <div className="flex justify-between items-start mb-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center group-hover:shadow-md transition-all duration-300 overflow-hidden">
                                                        {hospital.logoUrl ? (
                                                            <img src={hospital.logoUrl} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Building2 className={`w-5 h-5 ${t.textMuted}`} />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className={`font-bold text-base ${t.textWhite} leading-tight tracking-tight`}>{hospital.name}</h3>
                                                        <a href={`/${hospital.handle}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition-colors mt-0.5">
                                                            /{hospital.handle} <ExternalLink className="w-2.5 h-2.5" />
                                                        </a>
                                                    </div>
                                                </div>
                                                {/* Status dot */}
                                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${
                                                    hospital.metrics.screensOffline > 0
                                                        ? 'bg-amber-50 text-amber-600 border border-amber-200'
                                                        : 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                                }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${hospital.metrics.screensOffline > 0 ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.8)]'}`} />
                                                    {hospital.metrics.screensOffline > 0 ? 'Alerta' : 'Online'}
                                                </div>
                                            </div>

                                            {/* Metrics Grid */}
                                            <div className="grid grid-cols-4 gap-2 mb-5">
                                                <div className="bg-slate-50/70 border border-slate-100/80 p-3 rounded-xl">
                                                    <div className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-[0.15em] mb-1`}>Quiróf.</div>
                                                    <div className={`font-mono font-black ${t.textWhite} text-xl tracking-tight`}>{hospital.metrics.rooms}</div>
                                                </div>
                                                <div className="bg-slate-50/70 border border-slate-100/80 p-3 rounded-xl">
                                                    <div className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-[0.15em] mb-1`}>Pacientes</div>
                                                    <div className={`font-mono font-black ${t.textWhite} text-xl tracking-tight`}>{hospital.metrics.patients}</div>
                                                </div>
                                                <div className="bg-slate-50/70 border border-slate-100/80 p-3 rounded-xl">
                                                    <div className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-[0.15em] mb-1`}>TVs</div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="font-mono font-black text-emerald-600 text-xl tracking-tight">{hospital.metrics.screensOnline}</span>
                                                        {hospital.metrics.screensOffline > 0 && (
                                                            <span className="text-[10px] font-bold text-red-500">/{hospital.metrics.screensOffline}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="bg-slate-50/70 border border-slate-100/80 p-3 rounded-xl">
                                                    <div className={`text-[9px] ${t.textMuted} font-bold uppercase tracking-[0.15em] mb-1`}>Rating</div>
                                                    <div className={`font-mono font-black ${t.textWhite} text-xl tracking-tight flex items-center gap-0.5`}>
                                                        {hospital.metrics.avgRating ? (
                                                            <>
                                                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                                                {hospital.metrics.avgRating}
                                                            </>
                                                        ) : '—'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className={`flex gap-2.5 pt-5 ${t.borderTop}`}>
                                                <button
                                                    onClick={() => navigate(`/staff/paneladmin/analytics/${hospital.id}` + window.location.search)}
                                                    className="flex-1 py-2.5 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-bold transition-all duration-200 active:scale-[0.97] flex items-center justify-center gap-2"
                                                >
                                                    <BarChart2 className="w-4 h-4" /> Analytics
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/${hospital.handle}/staff` + window.location.search)}
                                                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.97] flex items-center justify-center gap-2"
                                                >
                                                    Entrar <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}

                                {/* Add Hospital Placeholder Card */}
                                <motion.div
                                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }}
                                    className="bg-white/50 rounded-2xl border-2 border-dashed border-slate-200 transition-all duration-300 flex flex-col items-center justify-center py-16 cursor-default group hover:border-indigo-300 hover:bg-indigo-50/20"
                                >
                                    <div className="p-4 rounded-2xl bg-slate-50 group-hover:bg-indigo-50 transition-all duration-300 mb-4">
                                        <Plus className={`w-8 h-8 ${t.textMuted} group-hover:text-indigo-500 transition-colors duration-300`} />
                                    </div>
                                    <p className={`text-sm font-bold ${t.textMuted} group-hover:text-indigo-500 transition-colors duration-300`}>Contacta al equipo para añadir una sucursal</p>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* SCREENS TAB                                */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'screens' && (
                        <div className={`animate-fade-in`}>
                            {/* Screen Stats Header */}
                            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-6`}>
                                <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} flex items-center gap-4`}>
                                    <div className={`p-3 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/30`}>
                                        <Monitor className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Online</p>
                                        <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                            {screens.filter(s => s.isOnline).length}
                                            <span className={`text-sm ${t.textMuted} font-medium ml-1`}>/ {screens.length}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} flex items-center gap-4`}>
                                    <div className={`p-3 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30`}>
                                        <Power className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Offline</p>
                                        <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                            {screens.filter(s => !s.isOnline).length}
                                        </p>
                                    </div>
                                </div>
                                <div className={`${t.cardBg} backdrop-blur-xl p-5 rounded-2xl border ${t.cardBorder} flex items-center gap-4`}>
                                    <div className={`p-3 bg-blue-500/10 text-blue-400 rounded-xl border border-blue-500/30`}>
                                        <LayoutGrid className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Hospitales con Pantallas</p>
                                        <p className={`text-2xl font-black ${t.textWhite} font-mono`}>
                                            {new Set(screens.map(s => s.hospitalId)).size}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Filters */}
                            <div className={`flex flex-wrap items-center gap-3 mb-6`}>
                                <div className={`flex ${t.cardBg} p-1 rounded-xl border ${t.cardBorder}`}>
                                    {(['all', 'online', 'offline'] as const).map(f => (
                                        <button
                                            key={f}
                                            onClick={() => setScreenFilter(f)}
                                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${screenFilter === f
                                                ? f === 'online' ? 'bg-emerald-600 text-white' : f === 'offline' ? 'bg-red-600 text-white' : 'bg-indigo-600 text-white'
                                                : `${t.textMuted} hover:${t.textWhite}`
                                                }`}
                                        >
                                            {f === 'all' ? 'Todas' : f === 'online' ? '🟢 Online' : '🔴 Offline'}
                                        </button>
                                    ))}
                                </div>
                                <select
                                    value={screenHospitalFilter || ''}
                                    onChange={e => setScreenHospitalFilter(e.target.value || null)}
                                    className={`text-sm ${t.inputBg} border ${t.cardBorder} rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium ${t.textWhite}`}
                                >
                                    <option value="">Todos los hospitales</option>
                                    {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                                </select>
                                <button
                                    onClick={async () => {
                                        try {
                                            const data = await storageService.getScreenSessions();
                                            setScreens(data as ScreenSession[]);
                                        } catch { setScreens([]); }
                                    }}
                                    className={`ml-auto px-4 py-2.5 ${t.cardBg} border ${t.cardBorder} ${t.textWhite} rounded-xl text-xs font-bold transition-all flex items-center gap-2`}
                                >
                                    <Activity className="w-3.5 h-3.5" /> Actualizar
                                </button>
                            </div>

                            {/* Screen Grid */}
                            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
                                {screens
                                    .filter(s => {
                                        if (screenFilter === 'online' && !s.isOnline) return false;
                                        if (screenFilter === 'offline' && s.isOnline) return false;
                                        if (screenHospitalFilter && s.hospitalId !== screenHospitalFilter) return false;
                                        return true;
                                    })
                                    .map(screen => {
                                        const lastSeen = new Date(screen.lastHeartbeat);
                                        const minutesAgo = Math.round((Date.now() - lastSeen.getTime()) / 60000);
                                        const timeLabel = minutesAgo < 1 ? 'Ahora' : minutesAgo < 60 ? `hace ${minutesAgo}m` : minutesAgo < 1440 ? `hace ${Math.round(minutesAgo / 60)}h` : `hace ${Math.round(minutesAgo / 1440)}d`;

                                        return (
                                            <div key={screen.id} className={`${t.cardBg} backdrop-blur-lg rounded-2xl border ${screen.isOnline ? 'border-emerald-500/30' : `${t.cardBorder}`} p-5 transition-all group relative overflow-hidden`}>
                                                {screen.isOnline && (
                                                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[60px] rounded-full pointer-events-none"></div>
                                                )}

                                                <div className="flex items-start justify-between mb-4 relative z-10">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`relative p-2.5 rounded-xl border ${screen.isOnline ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : `${t.cardBg} ${t.cardBorder} ${t.textMuted}`}`}>
                                                            {(() => {
                                                                const combined = `${screen.os || ''} ${screen.browser || ''} ${screen.userAgent || ''}`.toLowerCase();
                                                                let Icon = Monitor;
                                                                if (combined.includes('tizen') || combined.includes('webos') || combined.match(/\btv\b/) || combined.includes('roku') || combined.includes('chromecast')) Icon = Tv;
                                                                else if ((combined.includes('android') && combined.includes('mobile')) || combined.includes('iphone')) Icon = Smartphone;
                                                                else if (combined.includes('ipad') || (combined.includes('android') && !combined.includes('mobile')) || combined.includes('tablet')) Icon = Tablet;
                                                                else if (combined.includes('mac')) Icon = Laptop;
                                                                return <Icon className="w-5 h-5" />;
                                                            })()}
                                                            <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 ${theme === 'dark' ? 'border-slate-900' : 'border-white'} ${screen.isOnline ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-red-400'}`}>
                                                                {screen.isOnline && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75"></span>}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h4 className={`font-bold ${t.textWhite} text-sm leading-tight`}>{screen.screenName}</h4>
                                                            <p className={`text-[10px] ${t.textMuted} font-medium`}>{screen.hospitalName || screen.handle}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                        <button
                                                            onClick={() => setPreviewScreen(screen)}
                                                            className={`p-1.5 rounded-lg ${t.textMuted} hover:text-indigo-400 hover:bg-indigo-400/10 transition-all`}
                                                            title="Previsualizar pantalla"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditScreenName(screen.screenName);
                                                                setEditScreenGroup(screen.screenGroup || '');
                                                                setEditForcedTheme(screen.forcedTheme || null);
                                                                setEditScreenMode(screen.screenMode || 'BOARD');
                                                                const modeData = (screen as any).screenModeData || '';
                                                                if (screen.screenMode === 'MEDIA_URL') {
                                                                    setEditMediaUrl(modeData);
                                                                    setEditBoardLayout('LIST');
                                                                } else {
                                                                    setEditMediaUrl('');
                                                                    setEditBoardLayout(modeData.startsWith('boardLayout:') ? modeData.replace('boardLayout:', '') : 'LIST');
                                                                }
                                                                setEditingScreen(screen);
                                                            }}
                                                            className={`p-1.5 rounded-lg ${t.textMuted} hover:text-indigo-400 hover:bg-indigo-400/10 transition-all`}
                                                            title="Editar pantalla"
                                                        >
                                                            <Edit className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm(`¿Eliminar la pantalla "${screen.screenName}"?`)) {
                                                                    await storageService.deleteScreenSession(screen.id);
                                                                    setScreens(prev => prev.filter(s => s.id !== screen.id));
                                                                }
                                                            }}
                                                            className={`p-1.5 rounded-lg ${t.textMuted} hover:text-red-400 hover:bg-red-400/10 transition-all`}
                                                            title="Eliminar pantalla"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Metadata */}
                                                <div className={`space-y-2 text-xs relative z-10`}>
                                                    <div className={`flex justify-between ${t.textMuted}`}>
                                                        <span>Resolución</span>
                                                        <span className={`font-mono font-bold ${t.textMain}`}>{screen.resolution || '—'}</span>
                                                    </div>
                                                    <div className={`flex justify-between ${t.textMuted}`}>
                                                        <span>Tema</span>
                                                        <span className={`font-bold ${t.textMain} flex items-center gap-1`}>
                                                            {(screen.forcedTheme || screen.theme) === 'dark' ? <><Moon className="w-3 h-3" /> Oscuro</> : <><Sun className="w-3 h-3" /> Claro</>}
                                                            {screen.forcedTheme && <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1 rounded ml-1">FORZADO</span>}
                                                        </span>
                                                    </div>
                                                    <div className={`flex justify-between ${t.textMuted}`}>
                                                        <span>Heartbeat</span>
                                                        <span className={`font-bold ${screen.isOnline ? 'text-emerald-400' : 'text-red-400'}`}>{timeLabel}</span>
                                                    </div>

                                                    {/* Device info separator */}
                                                    <div className={`border-t ${t.cardBorder} my-2 !mt-3`}></div>

                                                    {screen.os && (
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Sistema</span>
                                                            <span className={`font-bold ${t.textMain}`}>{screen.os}</span>
                                                        </div>
                                                    )}
                                                    {screen.browser && (
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>Navegador</span>
                                                            <span className={`font-bold ${t.textMain}`}>{screen.browser}</span>
                                                        </div>
                                                    )}
                                                    {screen.ipAddress && (
                                                        <div className={`flex justify-between ${t.textMuted}`}>
                                                            <span>IP</span>
                                                            <span className={`font-mono font-bold ${t.textMain} text-[10px]`}>{screen.ipAddress}</span>
                                                        </div>
                                                    )}
                                                    <div className={`flex justify-between ${t.textMuted}`}>
                                                        <span>Primera conexión</span>
                                                        <span className={`font-mono ${t.textMain}`}>{new Date(screen.firstSeen).toLocaleDateString()}</span>
                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    })}
                                {screens.length === 0 && (
                                    <div className={`col-span-full py-12 text-center border-2 border-dashed ${t.cardBorder} rounded-2xl`}>
                                        <Monitor className={`w-12 h-12 ${t.textMuted} mx-auto mb-3 opacity-50`} />
                                        <h3 className={`text-lg font-bold ${t.textWhite}`}>Sin Pantallas Registradas</h3>
                                        <p className={`text-sm ${t.textMuted} mt-1 max-w-sm mx-auto`}>
                                            Cuando conectes pantallas en tus hospitales, aparecerán aquí.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* STAFF SESSIONS TAB                         */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'staff_sessions' && (
                        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                            <StaffSessionsPanel
                                hospitals={hospitals.map(h => ({ id: h.id, name: h.name }))}
                                primaryColor="#6366F1"
                                theme={theme}
                                onUnauthorized={handleLogout}
                            />
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* BILLING TAB                                */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'billing' && group && (
                        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                            <ClientBillingView
                                theme={theme}
                                groupId={group.id}
                                groupName={group.name}
                            />
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* MARKETING & REPUTACIÓN TAB                 */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'marketing' && (
                        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                            <ExecutiveMarketing
                                theme={theme}
                                hospitals={hospitals}
                                groupId={group?.id}
                                groupName={group?.name}
                            />
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* HISTORY TAB (Coming Soon Placeholder)      */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'history' && (
                        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex flex-col items-center justify-center py-20">
                            <div className="p-5 rounded-2xl bg-indigo-50 border border-indigo-200 inline-block mb-5">
                                <History className="w-10 h-10 text-indigo-400" />
                            </div>
                            <h3 className={`text-lg font-bold ${t.textWhite} mb-2`}>Historial de Cirugías</h3>
                            <p className={`text-sm ${t.textMuted} max-w-md text-center`}>
                                Próximamente: visualiza el historial completo de cirugías de todas tus sucursales con filtros avanzados, exportación a CSV y tiempos por estación.
                            </p>
                            <span className="mt-4 text-[10px] font-bold px-3 py-1 rounded-full bg-indigo-50 text-indigo-500 border border-indigo-200 uppercase tracking-widest">Fase 2</span>
                        </motion.div>
                    )}

                    {/* ══════════════════════════════════════════ */}
                    {/* ANALYTICS TAB (Coming Soon Placeholder)    */}
                    {/* ══════════════════════════════════════════ */}
                    {activeTab === 'history' && (
                        <div className={`bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden animate-fade-in`}>
                            <div className="p-6 sm:p-8 border-b border-slate-100">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                                            <History className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-black text-slate-800">Historial de Pacientes</h2>
                                            <p className="text-sm text-slate-500 font-medium">Historial completo de la cadena</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-end gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1.5 block">Hospital</label>
                                        <select
                                            value={historyFilterHospital || ''}
                                            onChange={e => setHistoryFilterHospital(e.target.value || null)}
                                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium text-slate-700 shadow-sm transition-all"
                                        >
                                            <option value="">Todos los hospitales</option>
                                            {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex-1 min-w-[150px]">
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1.5 block">Fecha de Ingreso (Desde)</label>
                                        <input
                                            type="date"
                                            value={historyDateRange.start}
                                            onChange={e => setHistoryDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium text-slate-700 shadow-sm transition-all"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-[150px]">
                                        <label className="text-[10px] uppercase font-bold text-slate-500 mb-1.5 block">Fecha de Ingreso (Hasta)</label>
                                        <input
                                            type="date"
                                            value={historyDateRange.end}
                                            onChange={e => setHistoryDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            className="w-full text-sm bg-white border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium text-slate-700 shadow-sm transition-all"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-100 bg-slate-50/50">
                                            <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Paciente</th>
                                            <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Procedimiento / Doctor</th>
                                            <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Hospital</th>
                                            <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Ingreso</th>
                                            <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {historyLoading ? (
                                            <tr>
                                                <td colSpan={5} className="py-12 text-center text-slate-500 font-medium">
                                                    <div className="flex justify-center mb-4">
                                                        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                                    </div>
                                                    Cargando historial...
                                                </td>
                                            </tr>
                                        ) : filteredHistory.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-12 text-center text-slate-500 font-medium">
                                                    No se encontraron pacientes
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredHistory.map(patient => {
                                                const hospitalName = hospitals.find(h => h.id === patient.hospitalId)?.name || patient.hospitalId;
                                                return (
                                                    <tr key={patient.id} className="hover:bg-slate-50 transition-colors">
                                                        <td className="py-4 px-6">
                                                            <div className="font-bold text-slate-800">{patient.name || 'Anónimo'}</div>
                                                            <div className="text-[10px] font-bold text-slate-400 font-mono mt-0.5">{patient.id}</div>
                                                        </td>
                                                        <td className="py-4 px-6">
                                                            <div className="font-bold text-slate-700">{patient.procedureName || 'N/A'}</div>
                                                            <div className="text-xs text-slate-500 mt-0.5">{patient.doctorName || 'N/A'}</div>
                                                        </td>
                                                        <td className="py-4 px-6 font-medium text-slate-600">{hospitalName}</td>
                                                        <td className="py-4 px-6">
                                                            <div className="font-bold text-slate-700">
                                                                {new Date(patient.admissionTime).toLocaleDateString()}
                                                            </div>
                                                            <div className="text-xs text-slate-500 mt-0.5">
                                                                {new Date(patient.admissionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-6">
                                                            <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600`}>
                                                                {patient.currentStatus || 'WAITING'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <GroupAnalytics hospitals={hospitals} groupHandle={handle || ''} />
                    )}

                </div>

                {/* Screen Edit Modal */}
                {editingScreen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingScreen(null)}>
                        <div className={`${theme === 'dark' ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'} border rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()}>
                            <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'} flex items-center justify-between shrink-0`}>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400 border border-indigo-500/20">
                                        <Monitor className="w-5 h-5" />
                                    </div>
                                    <h3 className={`text-lg font-bold ${t.textWhite}`}>Editar Pantalla</h3>
                                </div>
                                <button onClick={() => setEditingScreen(null)} className={`${t.textMuted} hover:${t.textWhite} p-1`}><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-5 overflow-y-auto flex-1">
                                <div 
                                    className="w-full relative rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 bg-black mb-5" 
                                    style={{ paddingTop: '56.25%' }}
                                    ref={el => {
                                        if (el && !el.dataset.observed) {
                                            el.dataset.observed = "true";
                                            const observer = new ResizeObserver(entries => {
                                                const width = entries[0].contentRect.width;
                                                const iframe = el.querySelector('iframe');
                                                if (iframe) iframe.style.transform = `scale(${width / 1280})`;
                                            });
                                            observer.observe(el);
                                        }
                                    }}
                                >
                                    <div className="absolute inset-0 w-full h-full overflow-hidden">
                                        <iframe
                                            src={`/${editingScreen.handle}/board?screenId=${editingScreen.id}&preview=true&themeOverride=${editForcedTheme || ''}&modeOverride=${editScreenMode}&layoutOverride=${editBoardLayout}`}
                                            className="absolute top-0 left-0 pointer-events-none border-0 max-w-none"
                                            style={{ width: '1280px', height: '720px', transformOrigin: '0 0' }}
                                            title="Screen Mini Preview"
                                        />
                                    </div>
                                    <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded text-[10px] text-white font-mono flex items-center gap-1.5 z-10 border border-white/10 shadow-lg">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span> PREVIEW
                                    </div>
                                </div>

                                {/* Name */}
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Nombre de la Pantalla</label>
                                    <input
                                        type="text"
                                        value={editScreenName}
                                        onChange={e => setEditScreenName(e.target.value)}
                                        placeholder="Ej: TV Lobby Principal"
                                        className={`w-full ${t.inputBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textWhite} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                    />
                                </div>
                                {/* Forced Theme */}
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>Tema Forzado</label>
                                    <div className="flex gap-2">
                                        {[
                                            { value: null, label: 'Auto', icon: null },
                                            { value: 'dark', label: 'Oscuro', icon: Moon },
                                            { value: 'light', label: 'Claro', icon: Sun },
                                        ].map(opt => (
                                            <button
                                                key={opt.label}
                                                onClick={() => setEditForcedTheme(opt.value)}
                                                className={`flex-1 py-2.5 px-3 rounded-xl border text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${editForcedTheme === opt.value
                                                    ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-600/20'
                                                    : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:${t.textWhite}`
                                                    }`}
                                            >
                                                {opt.icon && <opt.icon className="w-3.5 h-3.5" />}
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className={`text-[10px] ${t.textMuted} mt-2`}>
                                        {editForcedTheme ? 'La pantalla aplicará este tema en su próximo heartbeat.' : 'La pantalla usa el tema de su URL o configuración local.'}
                                    </p>
                                </div>
                                
                                {/* Screen Mode Picker */}
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-3 block`}>Modo de Operación</label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {([
                                            { id: 'BOARD', label: 'Médico', desc: 'Pacientes' },
                                            { id: 'FULL_ADS', label: 'Promos', desc: 'Slideshow' },
                                            { id: 'CLOCK', label: 'Reloj Zen', desc: 'Descanso' },
                                            { id: 'MEDIA_URL', label: 'Video', desc: 'YouTube' },
                                        ] as const).map(opt => (
                                            <button
                                                key={opt.id}
                                                onClick={() => setEditScreenMode(opt.id)}
                                                className={`flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-xl border transition-all text-center ${
                                                    editScreenMode === opt.id
                                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-400'
                                                        : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:border-indigo-500/40`
                                                }`}
                                            >
                                                <span className="text-[11px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                                <span className="text-[9px] opacity-50 leading-none mt-1">{opt.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                    
                                    {/* URL Input for MEDIA_URL mode */}
                                    {editScreenMode === 'MEDIA_URL' && (
                                        <div className="mt-4 p-4 rounded-xl border bg-black/20 border-white/5">
                                            <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-2 block`}>URL del Video (YouTube / MP4)</label>
                                            <input
                                                type="text" value={editMediaUrl} onChange={e => setEditMediaUrl(e.target.value)}
                                                placeholder="https://youtube.com/watch?v=..."
                                                className={`w-full ${t.cardBg} border ${t.cardBorder} rounded-xl px-4 py-3 ${t.textMain} text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50`}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Board Layout Picker (Only if BOARD mode) */}
                                {editScreenMode === 'BOARD' && (
                                <div>
                                    <label className={`text-xs font-bold ${t.textMuted} uppercase tracking-widest mb-3 block`}>Layout del Tablero</label>
                                    <div className="grid grid-cols-5 gap-2">
                                        {([
                                            { id: 'LIST', label: 'Lista', desc: 'Clásico' },
                                            { id: 'GRID_2COL', label: 'Grid 2×', desc: 'Cards' },
                                            { id: 'SIDEBAR_AD', label: 'Con Ad', desc: '70/30' },
                                            { id: 'FOCUS_SINGLE', label: 'Foco', desc: '1 a la vez' },
                                            { id: 'TICKER_ONLY', label: 'Ticker', desc: 'Marquesina' },
                                        ] as const).map(opt => (
                                            <button
                                                key={opt.id}
                                                onClick={() => setEditBoardLayout(opt.id)}
                                                className={`flex flex-col items-center gap-1 py-3 px-1 rounded-xl border transition-all text-center ${
                                                    editBoardLayout === opt.id
                                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-400'
                                                        : `${t.cardBg} ${t.cardBorder} ${t.textMuted} hover:border-indigo-500/40`
                                                }`}
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-wide leading-none">{opt.label}</span>
                                                <span className="text-[8px] opacity-50 leading-none mt-0.5">{opt.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <p className={`text-[10px] ${t.textMuted} mt-2`}>
                                        {editBoardLayout === 'LIST' && 'Lista de filas clásica — alta densidad de información.'}
                                        {editBoardLayout === 'GRID_2COL' && 'Cards premium en cuadrícula — visual e impactante.'}
                                        {editBoardLayout === 'SIDEBAR_AD' && 'Pacientes a la izquierda (70%) + panel de anuncio siempre visible (30%).'}
                                        {editBoardLayout === 'FOCUS_SINGLE' && 'Un paciente ocupa toda la pantalla — rotación automática cada 8 segundos.'}
                                        {editBoardLayout === 'TICKER_ONLY' && 'Pantalla con branding del hospital + ticker de estatus en la parte inferior.'}
                                    </p>
                                </div>
                                )}
                                
                                {/* Device info (read-only) */}
                                <div className={`${t.cardBg} border ${t.cardBorder} rounded-xl p-4 space-y-2`}>
                                    <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest mb-2`}>Información del Dispositivo</p>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className={t.textMuted}>Sistema</div>
                                        <div className={`font-bold ${t.textMain} text-right`}>{editingScreen.os || '—'}</div>
                                        <div className={t.textMuted}>Navegador</div>
                                        <div className={`font-bold ${t.textMain} text-right`}>{editingScreen.browser || '—'}</div>
                                        <div className={t.textMuted}>IP</div>
                                        <div className={`font-mono font-bold ${t.textMain} text-right text-[10px]`}>{editingScreen.ipAddress || '—'}</div>
                                        <div className={t.textMuted}>Resolución</div>
                                        <div className={`font-mono font-bold ${t.textMain} text-right`}>{editingScreen.resolution || '—'}</div>
                                    </div>
                                </div>
                            </div>
                            <div className={`p-6 pt-0 flex gap-3`}>
                                <button onClick={() => setEditingScreen(null)} className={`flex-1 py-3 ${t.cardBg} border ${t.cardBorder} ${t.textMuted} rounded-xl font-bold text-sm`}>
                                    Cancelar
                                </button>
                                <button
                                    onClick={async () => {
                                        if (!editScreenName.trim()) return;
                                        let finalScreenModeData: string | null = null;
                                        if (editScreenMode === 'BOARD' && editBoardLayout !== 'LIST') {
                                            finalScreenModeData = `boardLayout:${editBoardLayout}`;
                                        } else if (editScreenMode === 'MEDIA_URL') {
                                            finalScreenModeData = editMediaUrl.trim() || null;
                                        }

                                        const ok = await storageService.updateScreenSession(editingScreen.id, {
                                            screenName: editScreenName.trim(),
                                            screenGroup: editScreenGroup.trim() || null,
                                            forcedTheme: editForcedTheme,
                                            screenMode: editScreenMode as any,
                                            screenModeData: finalScreenModeData,
                                        });
                                        if (ok) {
                                            setScreens(prev => prev.map(s => s.id === editingScreen.id
                                                ? { ...s, screenName: editScreenName.trim(), screenGroup: editScreenGroup.trim() || null, forcedTheme: editForcedTheme, screenMode: editScreenMode as any, screenModeData: finalScreenModeData, isRegistered: true }
                                                : s
                                            ));
                                            setEditingScreen(null);
                                        }
                                    }}
                                    className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-600/20"
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* ─── Preview Screen Modal ───────────────────────── */}
                {previewScreen && (
                    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[100] flex flex-col items-center justify-center p-4 sm:p-8 animate-fade-in">
                        <div className="w-full max-w-6xl flex justify-between items-center mb-4">
                            <div>
                                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                                    <Monitor className="w-5 h-5 text-indigo-400" />
                                    {previewScreen.screenName}
                                </h3>
                                <p className="text-white/50 text-sm">Transmisión en vivo de la pantalla ({previewScreen.resolution || 'Auto'})</p>
                            </div>
                            <button onClick={() => setPreviewScreen(null)} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-all">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="w-full max-w-6xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative border border-white/10">
                            <iframe
                                src={`/${previewScreen.handle}/board?screenId=${previewScreen.id}`}
                                className="w-full h-full border-0 pointer-events-none"
                                title="Screen Preview"
                            />
                        </div>
                        <div className="mt-4 flex gap-4">
                            <p className="text-white/40 text-xs font-mono uppercase tracking-widest flex items-center justify-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> LIVE STREAM
                            </p>
                        </div>
                    </div>
                )}

                {/* Discrete Bug Report Footer */}
                <div className={`text-center py-6 mt-12 border-t ${t.borderTop}`}>
                    <BugReportWidget hospitalName={group?.name} />
                </div>
            </main>
        </div>
    );
};

export default GroupAdminDashboard;
